/**
 * Knowledge Archive concept graph - Obsidian-style Canvas renderer
 * powered by `react-force-graph-2d`.
 *
 * Design notes:
 *   - Uniform muted node color, size encodes importance. Hovered node
 *     plus its 1-degree neighbourhood stays vivid; everything else
 *     fades. Selected node carries a persistent indigo ring.
 *   - Labels appear only above a zoom threshold so the overview stays
 *     calm and turns informative as the user zooms in.
 *   - `warmupTicks` runs the force simulation invisibly before the
 *     first paint so the layout appears already settled - no "circle
 *     then spread out" animation. `cooldownTicks` freezes the sim once
 *     it's visible, freeing the main thread.
 *   - Canvas rendering is ~10× faster than the previous SVG library
 *     (react-d3-graph) and tops out comfortably above the 300-node cap.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ForceGraphMethods } from 'react-force-graph-2d';
import ForceGraph2D from 'react-force-graph-2d';
import { useIsDarkMode } from '@/hooks/ui/themeClass';
import { GraphHelpModal } from '@/components/features/mindspace/graph/GraphHelpModal';
import {
  drawLabel,
  drawClusterLabels,
  drawRestingLabels,
} from '@/components/features/mindspace/graph/canvasLabels';
import { GraphControls } from '@/components/features/mindspace/graph/GraphControls';
import { GraphHoverCard } from '@/components/features/mindspace/graph/GraphHoverCard';
import { ConstellationGuide } from '@/components/features/mindspace/graph/ConstellationGuide';
import { SpaceBackdrop } from '@/components/features/mindspace/graph/SpaceBackdrop';
import { fixedNodePosition } from '@/lib/mindspace/fixedGraphLayout';
import {
  layoutClusters,
  type ClusterCircle,
} from '@/lib/mindspace/clusterLayout';
import { isInRect, visibleRect } from '@/lib/mindspace/graphLod';
import { useGraphLod } from '@/hooks/mindspace/useGraphLod';
import type { LinkKind } from '@/lib/mindspace/decorateGraph';
import type { MemoryTier } from '@/lib/mindspace/memoryTier';
import {
  LABEL_VISIBLE_ZOOM,
  PHONE_LABEL_ZOOM,
} from '@/lib/mindspace/graphViewport';
import {
  FADE_ALPHA,
  GLOW_ALPHA_DARK,
  GLOW_ALPHA_LIGHT,
  LESSON_HUES,
  LINK_DIM_DARK,
  LINK_DIM_LIGHT,
  LINK_FADED_DARK,
  LINK_FADED_LIGHT,
  LINK_FOCUS_DARK,
  LINK_FOCUS_LIGHT,
  ORPHAN_ALPHA,
  PAUSED_ALPHA,
  canvasPalette,
} from '@/components/features/mindspace/graph/graphPalette';
import { useGraphAutoFit } from '@/hooks/mindspace/useGraphAutoFit';
import { useGlideToSelection } from '@/hooks/mindspace/useGlideToSelection';
import { assignLessonHues } from '@/lib/mindspace/lessonHues';
import { segmentState } from '@/lib/mindspace/constellation';
import { BLOB_SPREAD, blobOutline } from '@/lib/mindspace/lessonBlob';
import { scaleAlpha, useRevealFade } from '@/hooks/mindspace/useRevealFade';

// ============================================================================
// PUBLIC TYPES - kept stable so the page doesn't have to change.
// ============================================================================

export interface MindspaceGraphNode {
  id: string;
  name: string;
  /** Canvas radius in pixels (decorate-side computed from importance). */
  size?: number;
  /** Source lesson(s) gone → render in muted orphan style. */
  isOrphan?: boolean;
  /** Paused from reviews: a hollow ring rather than a filled dot. */
  isPaused?: boolean;
  notStarted?: boolean;
  /** Chance of recall now, for the hover card; see `decorateGraph`. */
  recall?: number | null;
  recallEstimated?: boolean;
  /** Memory state, drawn as the node's colour; see `graphPalette`. */
  tier?: MemoryTier;
  /** The cluster this node sits in. */
  subject: string;
  /** Orders a cluster: the most important concept is its centre. */
  importance: number | null;
  /** Set by the layout; the level-of-detail gate thins a cluster by it. */
  rank?: number;
  // Written back by the library; we also pre-set them from the layout.
  x?: number;
  y?: number;
}

export interface MindspaceGraphLink {
  source: string;
  target: string;
  /** Similarity score 0-1, meaningful for `similarity` links only:
   *  co-occurrence edges are all written at a hardcoded 1.0. */
  weight?: number;
  /** Decides whether the line is drawn at all. See `linkIsVisible`. */
  kind?: LinkKind;
}

export interface MindspaceGraphData {
  nodes: MindspaceGraphNode[];
  links: MindspaceGraphLink[];
  /** Per lesson, its concepts in teaching order (from the API; not drawn). */
  lessonPaths?: Record<string, string[]>;
}

interface MindspaceGraphProps {
  data: MindspaceGraphData;
  /** Currently-open concept (sidebar). Renders with a persistent ring. */
  selectedConceptId?: string | null;
  onNodeClick?: (nodeId: string) => void;
  /**
   * `'fixed'` freezes the layout: nodes are pinned to a ring by index, the
   * simulation never runs, and the viewport is pinned at 1× centred on the
   * origin. A test seam, not a feature — the same contract
   * `GridGlyph404.freezeAt` documents.
   *
   * It exists because this component paints to a canvas, so a node has no DOM
   * element to query and its position is otherwise the output of a few hundred
   * physics ticks. Frozen, the position is arithmetic
   * (`canvasCentre + fixedNodePosition(i, n)`), which is what lets a story
   * click a specific concept and gives the graph a Chromatic baseline that
   * does not change on every run. See `lib/mindspace/fixedGraphLayout`.
   *
   * Defaults to `'clustered'`, which is what the product uses: every node
   * pinned by `layoutClusters` — one group per subject, concepts placed on a
   * golden-angle spiral. There is no `'force'` any more. The old
   * simulation could not settle because the backend writes a co-occurrence
   * edge between every pair of concepts in a lesson at `weight=1.0`, which
   * this component mapped to the SHORTEST link distance; every lesson pulled
   * itself into a knot. Position is assigned now, not negotiated.
   */
  layout?: 'clustered' | 'fixed';
  /**
   * `'phone'` is the graph for a finger. There is no hover, so the SELECTED
   * concept takes hover's job - it and its links stay lit, everything else
   * fades - and their names show at any zoom: at the scale that fits a whole
   * graph on a 390px screen the labels are off, and a field of unnamed dots
   * is not a map. The camera glides to a tapped concept; node drag is off, so
   * a finger on a node still pans; the controls are `GraphPhoneControls`.
   */
  variant?: 'default' | 'phone';
  /** A tap or click on empty canvas. The phone uses it to put the peek away. */
  onBackgroundClick?: () => void;
  /**
   * Concepts named at ANY zoom while nothing is selected - the phone's few
   * biggest hubs, like the cities a zoomed-out map still names. Every name
   * at that scale would be a pile of overlapping pills.
   */
  restingLabelIds?: ReadonlySet<string>;
  /**
   * Cluster key → the name to paint over it. Clusters are keyed by LESSON ID
   * (see `lessonKeyOf`), and ids arrive before titles do, so the label is
   * resolved here rather than baked into the layout. A key with no entry is
   * not labelled at all — a raw UUID must never reach the canvas.
   */
  subjectLabels?: ReadonlyMap<string, string>;
}

// ============================================================================
// VISUAL CONSTANTS
// ============================================================================

// Collision and gravity constants lived here for the force simulation. Both
// are gone: `clusterLayout` guarantees separation arithmetically (adjacent
// nodes sit exactly NODE_SPACING apart) and places clusters with `pack()`, so
// nothing needs to be negotiated at runtime.

/**
 * Curvature, by whether the link leaves its cluster.
 *
 * A flat 0.15 on everything is what made links read as ribbons swooshing
 * across the canvas. A line between two concepts a few pixels apart wants to
 * be almost straight, the way a graph draws it; a line to another cluster gets
 * a touch more, which is what keeps two links between the same pair of
 * clusters distinguishable.
 *
 * Both are small, and the cross-cluster one especially. Curvature is a
 * FRACTION OF THE LINK'S LENGTH, so the same value that reads as a pleasant
 * arc between neighbours bows a cluster-to-cluster line hundreds of pixels out
 * of its way once you have zoomed in — 0.3 drew sweeping curves across the
 * whole viewport whose endpoints were both off screen.
 */
/**
 * Concept names drawn at once, past the zoom threshold.
 *
 * A cap on top of collision rejection, because rejection alone still tiles a
 * readable wall of ~150 pills across a 1440px canvas and those pills sit over
 * the links. Names are a scarce resource here: the graph's job at this zoom is
 * to show what connects to what, and a name is only worth it where it does not
 * cost you an edge.
 */
const MAX_CONCEPT_LABELS = 45;

/** The lesson-glow image's longest side, in pixels. */
const GLOW_MAX_PX = 1600;
/** How far a concept's dark-mode glow reaches, in radii; a star's further. */
const GLOW_REACH = 2.8;
const STAR_GLOW_REACH = 4.5;

/**
 * A concept as a star, for dark mode: a soft halo in its colour fading into
 * the sky, then the dot itself lit from within, near-white at the centre
 * melting into its memory colour at the rim, so it reads as a point of light
 * rather than a flat disc. A constellation's stars also get a faint
 * four-point sparkle, like a bright star in a photograph. The colour still
 * carries the memory state: the halo, the rim and the sparkle are all in it.
 */
function drawStar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  colour: string,
  { constellation, k }: { constellation: boolean; k: number }
) {
  const reach = size * (constellation ? STAR_GLOW_REACH : GLOW_REACH);
  const halo = ctx.createRadialGradient(x, y, size * 0.5, x, y, reach);
  halo.addColorStop(0, withAlpha(colour, constellation ? 0.42 : 0.22));
  halo.addColorStop(0.4, withAlpha(colour, constellation ? 0.12 : 0.06));
  halo.addColorStop(1, withAlpha(colour, 0));
  ctx.beginPath();
  ctx.arc(x, y, reach, 0, 2 * Math.PI);
  ctx.fillStyle = halo;
  ctx.fill();

  if (constellation) {
    // The sparkle: two thin rays, brightest at the star, gone at the tips.
    const ray = size * 3.6;
    const width = Math.max(0.5 / k, size * 0.16);
    for (const [dx, dy] of [
      [1, 0],
      [0, 1],
    ]) {
      const g = ctx.createLinearGradient(
        x - dx * ray,
        y - dy * ray,
        x + dx * ray,
        y + dy * ray
      );
      g.addColorStop(0, withAlpha(colour, 0));
      g.addColorStop(0.5, 'rgba(255, 255, 255, 0.55)');
      g.addColorStop(1, withAlpha(colour, 0));
      ctx.beginPath();
      ctx.moveTo(x - dx * ray, y - dy * ray);
      ctx.lineTo(x + dx * ray, y + dy * ray);
      ctx.lineWidth = width;
      ctx.strokeStyle = g;
      ctx.stroke();
    }
  }

  const core = ctx.createRadialGradient(x, y, 0, x, y, size);
  core.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
  core.addColorStop(0.45, colour);
  core.addColorStop(1, colour);
  ctx.beginPath();
  ctx.arc(x, y, size, 0, 2 * Math.PI);
  ctx.fillStyle = core;
  ctx.fill();
}

/** A `#rrggbb` colour as `rgba(...)` at `alpha`, for gradient stops. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** How strong the glows stay while a concept is focused. */
const GLOW_FOCUS_ALPHA = 0.35;
/** How strong other lessons' constellations stay while a concept is focused. */
const CONSTELLATION_FOCUS_ALPHA = 0.3;
/** ...and the focused concept's own lesson's, faded right down so its links
 *  read clearly over it. */
const CONSTELLATION_OWN_FOCUS_ALPHA = 0.12;

/**
 * A lesson blob's base radius from its layout circle. The outline swells up
 * to `BLOB_SPREAD` past this, so a blob reaches a little beyond its room and
 * neighbours blend where they meet.
 */
const blobBase = (r: number) => r * 0.9;

/** Gap between a lesson's topmost concept and its name, in graph units. */
const LABEL_LIFT = 16;

const WITHIN_CURVATURE = 0.04;
const ACROSS_CURVATURE = 0.08;

const FRAME = {
  default: 'relative h-full w-full rounded-lg border border-border',
  // Edge to edge: the canvas IS the page body on a phone, and `touch-none`
  // keeps a pinch on the map from zooming the page.
  phone: 'relative h-full w-full touch-none',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function MindspaceGraph({
  data,
  selectedConceptId,
  onNodeClick,
  layout = 'clustered',
  variant = 'default',
  onBackgroundClick,
  restingLabelIds,
  subjectLabels,
}: MindspaceGraphProps) {
  const isFixed = layout === 'fixed';
  const phone = variant === 'phone';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphRef = useRef<ForceGraphMethods | undefined>(undefined);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Scale AND the graph coordinate at the canvas centre. The centre is what
  // the viewport gate needs, and `onZoom` already reports it, so there is no
  // reason to reach into the ref for it.
  const [camera, setCamera] = useState({ k: 1, cx: 0, cy: 0 });
  const zoom = camera.k;
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const isDark = useIsDarkMode();
  const labelZoom = phone ? PHONE_LABEL_ZOOM : LABEL_VISIBLE_ZOOM;
  /**
   * The concept being asked about, and the single source for every part of
   * the answer: which links are drawn, which nodes keep their colour, how the
   * links are painted, and whether the resting labels step aside.
   *
   * Hover previews and a click pins, and they look identical because they are
   * one code path — the pointer wins while it is over something, the open
   * concept holds otherwise. They used to be split: the pivot was hover-only
   * on desktop and drove the fade and the link styling, while a click reached
   * `linkIsVisible` alone. So clicking a concept revealed its links to other
   * clusters and then drew them in resting grey at the resting width with
   * nothing faded around them — the reveal was there and invisible.
   *
   * A phone has no pointer, so the selection is the only focus it ever has.
   */
  const focusId = phone
    ? (selectedConceptId ?? null)
    : (hoveredId ?? selectedConceptId ?? null);

  // Every node carries `fx`/`fy`, which the force library treats as a hard
  // pin — the simulation may still tick but nothing can move. Copies rather
  // than mutating: `data` belongs to the caller, and the library writes
  // `x`/`y` back into whatever it is handed, so pinning in place would leave
  // the page's memoised graph permanently frozen the first time it rendered.
  //
  // Two sources of positions. `fixed` is the ring test seam; `clustered` is
  // the product layout.
  // The legend's "Paused" row only appears while there is a paused concept
  // on the map to explain.
  const hasPaused = useMemo(
    () => data.nodes.some(node => node.isPaused),
    [data.nodes]
  );

  /**
   * Where everything sits, computed ONCE per data change: lessons packed by
   * size, then pulled next to the lessons they share links with (see
   * `relaxClusters`). Only meaningful links count; co-occurrence is every
   * pair inside a lesson and says nothing about other lessons.
   */
  const placement = useMemo(() => {
    if (isFixed) return null;
    const links = data.links
      .filter(link => link.kind !== 'co_occurrence')
      .map(link => ({ source: idOf(link.source), target: idOf(link.target) }));
    return layoutClusters(data.nodes, links);
  }, [data, isFixed]);

  const graphData = useMemo(() => {
    if (!placement) {
      return {
        nodes: data.nodes.map((node, index) => {
          const { x, y } = fixedNodePosition(index, data.nodes.length);
          return { ...node, x, y, fx: x, fy: y };
        }),
        links: data.links,
      };
    }
    const placed = new Map(placement.nodes.map(node => [node.id, node]));
    return {
      nodes: data.nodes.map(node => {
        const at = placed.get(node.id);
        const x = at?.x ?? 0;
        const y = at?.y ?? 0;
        return { ...node, x, y, fx: x, fy: y, rank: at?.rank ?? 0 };
      }),
      links: data.links,
    };
  }, [data, placement]);

  /**
   * Your progress on each constellation on the map: of the lessons that take
   * it, the best one's share of key ideas (its stars) learned. A figure with
   * every star learned and none paused is COLLECTED. For the Constellations
   * panel, which explains them and shows how close each is.
   */
  const constellationProgress = useMemo(() => {
    const best = new Map<string, { learned: number; total: number }>();
    if (!placement) return best;
    const byId = new Map(data.nodes.map(n => [n.id, n]));
    for (const { name, lines } of Object.values(placement.constellations)) {
      const stars = [...new Set(lines.flat())];
      const learned = stars.filter(id => {
        const n = byId.get(id);
        return (
          n != null &&
          !n.isPaused &&
          (n.tier === 'strong' || n.tier === 'fresh')
        );
      }).length;
      const held = best.get(name);
      if (!held || learned / stars.length > held.learned / held.total)
        best.set(name, { learned, total: stars.length });
    }
    return best;
  }, [placement, data.nodes]);

  /** The concepts that are a constellation's stars: they glow brightest. */
  const starIds = useMemo(() => {
    const ids = new Set<string>();
    for (const { lines } of Object.values(placement?.constellations ?? {}))
      for (const [a, b] of lines) {
        ids.add(a);
        ids.add(b);
      }
    return ids;
  }, [placement]);

  /** Cluster centres, for the subject labels. Empty in the `fixed` seam. */
  const clusters = useMemo<ClusterCircle[]>(
    () => placement?.clusters ?? [],
    [placement]
  );

  /**
   * Which subject each concept belongs to, for the link accessors — a link
   * that leaves its cluster is drawn differently from one inside it.
   *
   * Built from `data.nodes` rather than read off the link endpoints, because
   * the library rewrites `source`/`target` from ids into node objects once it
   * owns the graph and the accessors see both shapes.
   */
  const subjectById = useMemo(
    () => new Map(data.nodes.map(n => [n.id, n.subject])),
    [data.nodes]
  );

  /**
   * O(1) neighbour lookup for the hover-highlight chain. The force lib
   * mutates link.source / link.target to be node objects (not ids) after the
   * simulation starts, so the lookup handles both string and object shapes
   * defensively.
   *
   * It skips `co_occurrence`, and that is not an optimisation — it is the same
   * rule `linkIsVisible` applies, and the two have to agree. They did not: the
   * chain counted every lesson-mate, so hovering one concept in a 30-concept
   * lesson left twenty-nine nodes at full brightness with the rest of the
   * canvas faded to 0.18 and NO LINE drawn to any of them, because
   * co-occurrence is never drawn. A highlight that claims a connection the
   * picture does not show is worse than no highlight.
   */
  const connectedToFocus = useMemo(() => {
    const s = new Set<string>();
    if (!focusId) return s;
    for (const link of data.links) {
      if (link.kind === 'co_occurrence') continue;
      const srcId = idOf(link.source);
      const tgtId = idOf(link.target);
      if (srcId === focusId) s.add(tgtId);
      if (tgtId === focusId) s.add(srcId);
    }
    return s;
  }, [focusId, data.links]);

  const { nodeIsVisible, linkIsVisible, visibleIds, drawnSubjects } =
    useGraphLod({
      nodes: graphData.nodes as MindspaceGraphNode[],
      clusters,
      camera,
      dimensions,
      disabled: isFixed,
      focusId,
      focusNeighbours: connectedToFocus,
      idOf,
    });

  // Resize tracking via ResizeObserver - robust under sidebar collapse
  // animation. Falls back to a single read on mount if the observer
  // hasn't fired yet.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const apply = () => {
      // `clientWidth`/`clientHeight`, NOT `getBoundingClientRect()`. The
      // wrapper is `border border-border`, and a bounding rect is the BORDER
      // box — so sizing the canvas from it made the canvas two pixels wider
      // and taller than the box it lives in, overflowing the rounded corners
      // it is supposed to sit inside.
      //
      // It also defeated the non-zero gate below. `box-sizing: border-box`
      // cannot shrink an element below its own borders, so a fully collapsed
      // container still measured 2×2 and the graph mounted a canvas two
      // pixels tall instead of waiting. The content box genuinely reaches zero.
      setDimensions({ width: el.clientWidth, height: el.clientHeight });
    };
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Tune the d3-force simulation for spacing + overlap-prevention.
  //
  //   - charge          stronger repulsion than the lib default (-30)
  //                     so nodes claim breathing room.
  //   - link.distance   variable per link: high similarity → shorter
  //                     rest length (related concepts settle closer).
  //   - collide         guarantees no two discs overlap; the radius
  //                     is NODE_RADIUS + buffer so labels never tangle.
  //   - x / y           weak gravity, so disconnected pieces stay close.
  //
  // Re-apply on every `data` reference change - NOT just when the node
  // *count* changes. The library wipes its forces back to defaults
  // whenever `graphData` mounts as a new reference (Context7-documented
  // behaviour), so if React Query returns a fresh array of the same
  // length our custom charge/link/collide settings would silently
  // revert. Depending on `data` directly catches every such reset.
  // Apply the force config as soon as the FG ref resolves.
  //
  // `<ForceGraph2D>` mounts behind a `dimensions.{width,height} > 0`
  // gate (we wait for the ResizeObserver to measure the container),
  // so on the first render `graphRef.current` is still null. The
  // dep-array can't usefully catch that moment - `data` may have
  // stopped changing by the time the ref resolves - so we poll on
  // requestAnimationFrame until it's ready, then run the force setup
  // exactly once per data change. Without this, the lib's default
  // forces (charge ≈ -30, no collide) silently win and the visible
  // layout has none of the spacing we configure here.
  // No force configuration: positions come from `layoutClusters` and every
  // node is pinned with `fx`/`fy`, so there is nothing to solve.

  // Frame every node — labels included, zoom capped — once the layout has
  // settled, and again whenever the node SET changes. See `useGraphAutoFit`.
  // Fixed mode pins the viewport instead (below): fitting would re-derive the
  // scale from the layout, which is exactly what that seam avoids.
  const autoFit = useGraphAutoFit({
    graphRef,
    // The PLACED nodes: `data.nodes` carry no positions, so a fit computed
    // from them found nothing to frame and the map opened wherever the
    // library's default camera happened to put it.
    nodes: graphData.nodes,
    width: dimensions.width,
    height: dimensions.height,
    enabled: !isFixed,
    labelZoom,
  });

  /**
   * Fixed mode's viewport: scale 1, graph origin at the centre of the canvas.
   *
   * Together with the pinned node positions this makes a node's pixel centre
   * `canvasRect centre + fixedNodePosition(i, n)`, with no library maths in
   * the story, and the badge reading 100% is the observable proof it took.
   *
   * **Enforced on every zoom event, not set once.** The library applies its own
   * node-count-derived scale (`4 / ∛nodes`) on its first update, and only while
   * the transform is still one it set itself — so whether a one-shot pin lands
   * before or after that is a race. It was written as a one-shot on
   * `onEngineStop` first, and that reliably lost under load: the badge read
   * 192%, which is exactly `4 / ∛9`. Re-asserting converges instead of racing,
   * and costs nothing, because the `k === 1` guard makes the settled case a
   * no-op rather than an event loop.
   *
   * `viewportReleased` is the deliberate way out. Re-fitting from the zoom
   * badge is real behaviour (§2.10 #25) and has to keep working, so asking for
   * a fit stops the enforcement for the life of the mount.
   */
  const viewportReleased = useRef(false);

  const enforceFixedViewport = useCallback(
    (k: number) => {
      if (!isFixed || viewportReleased.current || k === 1) return;
      graphRef.current?.zoom(1, 0);
      graphRef.current?.centerAt(0, 0, 0);
    },
    [isFixed]
  );

  const { fit: autoFitNow } = autoFit;
  const fitToViewport = useCallback(() => {
    viewportReleased.current = true;
    if (isFixed) graphRef.current?.zoomToFit(400, 60);
    else autoFitNow();
  }, [isFixed, autoFitNow]);

  // Fades for what the zoom reveals and for focus; see `useRevealFade`. Off
  // in the frozen seam, whose stories read exact pixels on the first frame.
  const { revealAlpha, focusProgress, animating } = useRevealFade({
    visibleIds,
    focusId,
    disabled: isFixed,
  });

  useGlideToSelection({
    graphRef,
    nodes: graphData.nodes,
    selectedId: selectedConceptId,
    // Not in the frozen seam: its whole contract is a camera that stays put.
    enabled: phone && !isFixed,
    onGlide: autoFit.release,
  });

  const handleNodeClick = useCallback(
    (node: { id?: string | number }) => {
      if (node?.id != null) onNodeClick?.(String(node.id));
    },
    [onNodeClick]
  );

  const handleNodeHover = useCallback(
    (node: { id?: string | number } | null) => {
      // A phone has no hover: a tap reports one, which would stick.
      if (phone) return;
      setHoveredId(node?.id != null ? String(node.id) : null);
    },
    [phone]
  );

  const openHelp = useCallback(() => setIsHelpModalOpen(true), []);

  /** The toolbar's +/−: a short eased step, and the camera is the user's. */
  const zoomBy = useCallback(
    (factor: number) => {
      const fg = graphRef.current;
      if (!fg) return;
      autoFit.release();
      fg.zoom(fg.zoom() * factor, 250);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoFit.release]
  );

  // The canvas paint callback fires per-node per-tick. Computed colours
  // are captured via closure so the renderer doesn't re-resolve them
  // each tick. Keep it tight.
  const {
    nodeTier,
    nodeInk,
    labelColor,
    labelPill,
    highlight,
    highlightSoft,
    ringGap,
  } = canvasPalette(isDark);

  /** The topmost visible concept of each lesson, for its name's place. */
  const visibleTop = useMemo(() => {
    const top = new Map<string, number>();
    for (const node of graphData.nodes) {
      if (!visibleIds.has(node.id) || node.y == null) continue;
      top.set(
        node.subject,
        Math.min(top.get(node.subject) ?? Infinity, node.y)
      );
    }
    return top;
  }, [graphData.nodes, visibleIds]);

  /** Which hue each lesson's glow takes; neighbours never share one. */
  const lessonHue = useMemo(
    () => assignLessonHues(clusters, LESSON_HUES.length),
    [clusters]
  );

  /**
   * Every lesson as a soft glow of its own hue, painted ONCE into a small
   * offscreen image and stretched under the map each frame.
   *
   * Each glow is a BLOB, never a circle: an outline that swells and dents in
   * a pattern seeded from the lesson's id (`blobOutline`), so every lesson has
   * its own organic shape and keeps it on every visit. It is filled with a
   * gradient brightest at the centre and fading towards the rim, then blurred,
   * so the edge melts into the card. Blobs reach a little past their lesson's
   * room, so neighbouring lessons (and lessons pulled together by shared
   * links) blend where they meet, like a heatmap. The image is low resolution
   * on purpose: drawing it scaled up keeps the gradients soft and cheap.
   */
  const glow = useMemo(() => {
    if (isFixed || clusters.length === 0) return null;
    if (typeof document === 'undefined') return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const c of clusters) {
      const reach = blobBase(c.r) * (1 + BLOB_SPREAD) * 1.3;
      minX = Math.min(minX, c.x - reach);
      minY = Math.min(minY, c.y - reach);
      maxX = Math.max(maxX, c.x + reach);
      maxY = Math.max(maxY, c.y + reach);
    }
    const w = maxX - minX;
    const h = maxY - minY;
    const scale = Math.min(0.25, GLOW_MAX_PX / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(w * scale));
    canvas.height = Math.max(1, Math.ceil(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Dark mode: a faint haze added as light over the nebula backdrop
    // (`SpaceBackdrop`), so each lesson still reads as its own patch of sky
    // without competing with the image. Light mode: a soft tint.
    ctx.globalCompositeOperation = isDark ? 'lighter' : 'source-over';
    const peak = isDark ? GLOW_ALPHA_DARK : GLOW_ALPHA_LIGHT;
    for (const c of clusters) {
      const hue = lessonHue.get(c.subject) ?? 0;
      const base = blobBase(c.r) * scale;
      paintBlob(
        ctx,
        c.subject,
        (c.x - minX) * scale,
        (c.y - minY) * scale,
        base,
        LESSON_HUES[hue],
        {
          stops: isDark
            ? [peak, peak * 0.6, 0]
            : [peak, peak * 0.75, peak * 0.35],
          blur: isDark ? 0.16 : 0.07,
        }
      );
    }
    ctx.filter = 'none';
    return { canvas, x: minX, y: minY, w, h };
  }, [isFixed, clusters, lessonHue, isDark]);

  /** The glows, under links and concepts. Fades back while one is focused. */
  const drawGlow = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!glow) return;
      ctx.save();
      ctx.globalAlpha = focusId != null ? GLOW_FOCUS_ALPHA : 1;
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(glow.canvas, glow.x, glow.y, glow.w, glow.h);
      ctx.restore();
    },
    [glow, focusId]
  );

  /**
   * Each lesson's constellation, like one in the night sky: its key ideas
   * are the stars, and the shortest set of non-crossing lines joins them
   * into a figure that is that lesson's own (see `constellationLines`).
   *
   * Zoomed out, the figures are what you see: each lesson's stars and lines,
   * the rest of its concepts not yet shown. Zooming in, the other concepts
   * fill in around them and the lines thin and soften, so the detail can
   * speak. Pointing at a concept fades its own lesson's figure right down,
   * so the lines to everything it connects to are the clear answer, and the
   * other figures step back too.
   *
   * A line is LIT when you have learned both stars it joins, dims once
   * either is fading or worth a review, and is dashed while either is not
   * studied, so a lesson's figure fills in as you learn it.
   */
  const drawConstellations = useCallback(
    (ctx: CanvasRenderingContext2D, k: number) => {
      if (!placement) return;
      const byId = new Map(graphData.nodes.map(n => [n.id, n]));
      const focusLesson =
        focusId != null ? (subjectById.get(focusId) ?? null) : null;
      // Full strength at overview, easing down as the zoom fills in detail.
      const zoomFade = k <= 0.7 ? 1 : Math.max(0.45, 1 - (k - 0.7) * 0.35);
      ctx.save();
      ctx.lineCap = 'round';
      ctx.strokeStyle = highlight;
      for (const [lesson, { lines }] of Object.entries(
        placement.constellations
      )) {
        const focusFade =
          focusLesson == null
            ? 1
            : lesson === focusLesson
              ? CONSTELLATION_OWN_FOCUS_ALPHA
              : CONSTELLATION_FOCUS_ALPHA;
        for (const [ida, idb] of lines) {
          const a = byId.get(ida);
          const b = byId.get(idb);
          if (!a || !b) continue;
          if (!visibleIds.has(a.id) || !visibleIds.has(b.id)) continue;
          const state = segmentState(a, b);
          const strength =
            state === 'lit' ? 0.6 : state === 'slipping' ? 0.32 : 0.26;
          ctx.globalAlpha = strength * zoomFade * focusFade;
          ctx.lineWidth = (state === 'lit' ? 1.6 : 1.2) / k;
          ctx.setLineDash(state === 'unlit' ? [3 / k, 4 / k] : []);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      ctx.restore();
    },
    [placement, graphData.nodes, focusId, subjectById, visibleIds, highlight]
  );

  // The resting map's names (`restingLabelIds`), drawn over the frame with
  // collisions skipped. Only while nothing is selected and the zoom has not
  // reached the point where every node is named anyway.
  /** Subject names over their clusters; see `drawClusterLabels`. */
  const drawSubjectLabels = useCallback(
    (ctx: CanvasRenderingContext2D, k: number) => {
      const fg = graphRef.current;
      if (!fg || isFixed || focusId != null || k >= labelZoom) return;
      const rect = visibleRect({ ...camera, ...dimensions });
      // Only the clusters the LOD actually drew. Fed every cluster, this pass
      // relied on collision rejection alone to thin hundreds of labels, which
      // is not thinning — it is whichever ones happened to be ordered first,
      // and it filled the canvas with pills. A name also has to belong to
      // something visible: labelling a group whose nodes were culled points at
      // empty space.
      // Resolve the display name here, and drop any cluster that has none:
      // the key is a lesson id, and an unresolved id is a UUID, which is
      // worse than no label at all. Titles arrive from a separate query, so
      // "none yet" is a normal state for the first frames.
      const named = clusters.flatMap(c => {
        if (!drawnSubjects.has(c.subject)) return [];
        const label = subjectLabels?.get(c.subject);
        if (!label) return [];
        // Just above the lesson's topmost VISIBLE concept, so the name stays
        // with the dots it names rather than floating at the glow's edge.
        const top = visibleTop.get(c.subject);
        const reach = top == null ? 0 : c.y - top + LABEL_LIFT;
        return [{ ...c, subject: label, reach }];
      });
      // Hand over to concept names: fade out across the last stretch before
      // `labelZoom`, where every concept gets its own name.
      const alpha = Math.min(
        1,
        Math.max(0, (labelZoom - k) / (labelZoom * 0.3))
      );
      drawClusterLabels(ctx, named, k, {
        colours: { text: nodeInk(1), halo: ringGap },
        left: fg.screen2GraphCoords(0, 0).x,
        right: fg.screen2GraphCoords(dimensions.width, 0).x,
        inView: (x, y) => isInRect(x, y, rect),
        alpha,
      });
    },
    [
      isFixed,
      focusId,
      labelZoom,
      clusters,
      drawnSubjects,
      subjectLabels,
      visibleTop,
      camera,
      dimensions,
      nodeInk,
      ringGap,
    ]
  );

  /**
   * Concept names, once zoomed in past `labelZoom` — as a post-pass with
   * collision rejection, not one label per node inside `drawNode`.
   *
   * Measured on a live archive: crossing 140% took the painted link area down
   * 40% while label-pill area went UP 187%. Every node on screen got a name,
   * ~300 of them, each with an `rgba(255,255,255,0.85)` pill, and since the
   * library paints links BEFORE nodes those pills blanket the edges. Zooming
   * in one notch turned a readable web of connections into a wall of
   * overlapping text — the "nodes appear and the edges are gone" report, and
   * it was never the level-of-detail gates at all.
   *
   * `drawRestingLabels` already places labels in priority order and drops any
   * that would collide, which is exactly what was missing; it just had never
   * been pointed at concept names. Highest importance first, so when there is
   * only room for some, the ones that get named are the ones worth naming.
   */
  const drawConceptLabels = useCallback(
    (ctx: CanvasRenderingContext2D, k: number) => {
      const fg = graphRef.current;
      if (!fg || k < labelZoom) return;
      const rect = visibleRect({ ...camera, ...dimensions });
      // While something is focused, name only its neighbourhood. The fade
      // pushes every other node to 0.18 alpha but this pass paints over the
      // top at full strength, so without it a focused graph is pale dots
      // under crisp labels — the labels end up louder than the answer.
      const inChain = (n: MindspaceGraphNode) =>
        focusId == null || n.id === focusId || connectedToFocus.has(n.id);
      const named = (graphData.nodes as MindspaceGraphNode[])
        .filter(
          n => visibleIds.has(n.id) && inChain(n) && isInRect(n.x, n.y, rect)
        )
        .sort(
          (a, b) =>
            (b.importance ?? -1) - (a.importance ?? -1) ||
            a.name.localeCompare(b.name)
        )
        .slice(0, MAX_CONCEPT_LABELS);
      drawRestingLabels(ctx, named, k, {
        colours: { pill: labelPill, text: labelColor },
        left: fg.screen2GraphCoords(0, 0).x,
        right: fg.screen2GraphCoords(dimensions.width, 0).x,
      });
    },
    [
      labelZoom,
      camera,
      dimensions,
      graphData,
      visibleIds,
      focusId,
      connectedToFocus,
      labelPill,
      labelColor,
    ]
  );

  const drawResting = useCallback(
    (ctx: CanvasRenderingContext2D, k: number) => {
      const fg = graphRef.current;
      if (!fg || !restingLabelIds?.size || focusId != null || k >= labelZoom)
        return;
      const byId = new Map(
        (graphData.nodes as MindspaceGraphNode[]).map(n => [n.id, n])
      );
      const hubs = [...restingLabelIds].flatMap(id => byId.get(id) ?? []);
      const left = fg.screen2GraphCoords(0, 0).x;
      const right = fg.screen2GraphCoords(dimensions.width, 0).x;
      drawRestingLabels(ctx, hubs, k, {
        colours: { pill: labelPill, text: labelColor },
        left,
        right,
      });
    },
    [
      restingLabelIds,
      focusId,
      labelZoom,
      graphData,
      dimensions.width,
      labelPill,
      labelColor,
    ]
  );

  const drawNode = useCallback(
    (
      node: MindspaceGraphNode,
      ctx: CanvasRenderingContext2D,
      globalScale: number
    ) => {
      if (node.x == null || node.y == null) return;
      const baseSize = node.size ?? 4;
      const isSelected =
        selectedConceptId != null && node.id === selectedConceptId;
      // Hover-pivot is JUST this node (not the chain) - Obsidian-style.
      // Connected nodes keep their base color but stay at full alpha;
      // only the actively-hovered + the selected node go indigo.
      const isHoveredSelf = focusId != null && node.id === focusId;
      const isConnectedToHover =
        focusId != null && connectedToFocus.has(node.id);
      const isInHighlightChain =
        isHoveredSelf || isConnectedToHover || isSelected;
      const isFaded = focusId != null && !isInHighlightChain;

      // No hover scale - node size stays uniform; colour + outline
      // (and the eventual sidebar) carry the "you're pointing at this
      // one" affordance instead.
      const size = baseSize;

      ctx.save();
      // Memory state is carried by COLOUR now, not by weight — see
      // `graphPalette`. Alpha is left for the two things that are not states:
      // a concept with nothing linked to it, and everything outside a focused
      // neighbourhood.
      // Faded nodes ease down from full strength as focus begins.
      const fadedAlpha = 1 - (1 - FADE_ALPHA) * focusProgress();
      ctx.globalAlpha =
        (isFaded ? fadedAlpha : node.isOrphan ? ORPHAN_ALPHA : 1) *
        revealAlpha(node.id);

      // Soft outer halo for selected node (always visible, even at rest).
      if (isSelected) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, size + 4 / globalScale, 0, 2 * Math.PI);
        ctx.fillStyle = highlightSoft;
        ctx.fill();
      }

      // A paused concept is a hollow ring: on hold, so it carries no memory
      // colour, and a SHAPE rather than a fifth colour so it cannot be
      // mistaken for any state in the legend. The ring is a share of the
      // radius, so it stays a ring at every zoom.
      if (node.isPaused) {
        const ring = size * 0.38;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
        ctx.fillStyle = ringGap;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(node.x, node.y, size - ring / 2, 0, 2 * Math.PI);
        ctx.lineWidth = ring;
        ctx.strokeStyle = nodeInk(PAUSED_ALPHA);
        ctx.stroke();
      } else {
        const fill = node.isOrphan ? nodeInk(1) : nodeTier(node.tier);
        if (isDark && !isFaded && !node.isOrphan) {
          drawStar(ctx, node.x, node.y, size, fill, {
            constellation: starIds.has(node.id),
            k: globalScale,
          });
        } else {
          // The node keeps its memory colour even while pointed at: that
          // colour is what the reader is pointing to find out. Focus is the
          // ring below.
          ctx.beginPath();
          ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
          ctx.fillStyle = fill;
          ctx.fill();
          // A crisp 1px ring in the card colour, where a blurred drop shadow
          // used to be: it separates two concepts that touch and keeps a
          // small dot sharp at any zoom. Light mode only: in dark mode the
          // concepts are stars, and a ring made them look like buttons.
          if (!isFaded) {
            ctx.lineWidth = 1 / globalScale;
            ctx.strokeStyle = ringGap;
            ctx.stroke();
          }
        }
      }

      // Focus ring, only on the hover pivot or the selection: a card-coloured
      // gap and then the brand ring, so it reads the same around an indigo,
      // grey or amber fill. Resting nodes stay bare fills.
      if (isHoveredSelf || isSelected) {
        drawFocusRing(ctx, node, size, globalScale, ringGap, highlight);
      }

      // The phone names the selection and its links at any zoom, because
      // there is no hover to ask "what is this?" with. The ZOOM-threshold
      // labels are not drawn here any more — see `drawConceptLabels`.
      if (phone && focusId != null && isInHighlightChain) {
        drawLabel(ctx, node, globalScale, {
          pill: labelPill,
          text: labelColor,
        });
      }

      ctx.restore();
    },
    [
      selectedConceptId,
      focusId,
      phone,
      connectedToFocus,
      nodeTier,
      nodeInk,
      labelColor,
      labelPill,
      highlight,
      highlightSoft,
      ringGap,
      focusProgress,
      revealAlpha,
      isDark,
      starIds,
    ]
  );

  // Generous pointer area - easier to click small nodes without zooming.
  const drawNodePointerArea = useCallback(
    (
      node: MindspaceGraphNode,
      color: string,
      ctx: CanvasRenderingContext2D
    ) => {
      if (node.x == null || node.y == null) return;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, (node.size ?? 4) + 4, 0, 2 * Math.PI);
      ctx.fill();
    },
    []
  );

  const getLinkColor = useCallback(
    (link: MindspaceGraphLink) => {
      const srcId = idOf(link.source);
      const tgtId = idOf(link.target);
      // A link fades in with whichever of its ends appeared last.
      const reveal = Math.min(revealAlpha(srcId), revealAlpha(tgtId));
      if (focusId && (srcId === focusId || tgtId === focusId)) {
        return scaleAlpha(isDark ? LINK_FOCUS_DARK : LINK_FOCUS_LIGHT, reveal);
      }
      if (focusId) {
        return scaleAlpha(isDark ? LINK_DIM_DARK : LINK_DIM_LIGHT, reveal);
      }
      return scaleAlpha(isDark ? LINK_FADED_DARK : LINK_FADED_LIGHT, reveal);
    },
    [focusId, isDark, revealAlpha]
  );

  /** True when a link leaves its cluster: a long line across open space. */
  const crossesClusters = useCallback(
    (link: MindspaceGraphLink) => {
      const from = subjectById.get(idOf(link.source));
      const to = subjectById.get(idOf(link.target));
      return from != null && to != null && from !== to;
    },
    [subjectById]
  );

  const getLinkCurvature = useCallback(
    (link: MindspaceGraphLink) =>
      crossesClusters(link) ? ACROSS_CURVATURE : WITHIN_CURVATURE,
    [crossesClusters]
  );

  const getLinkWidth = useCallback(
    (link: MindspaceGraphLink) => {
      if (!focusId) return 0.6;
      const srcId = idOf(link.source);
      const tgtId = idOf(link.target);
      const touchesHover = srcId === focusId || tgtId === focusId;
      return touchesHover ? 1.2 : 0.4;
    },
    [focusId]
  );

  // §2.10 #22 — the canvas mounts only once the container has been measured.
  // `ForceGraph2D` reads width/height once at construction and sizes its
  // backing store from them, so mounting at 0×0 produces a canvas that never
  // paints however the container is resized afterwards.
  const isMeasured = dimensions.width > 0 && dimensions.height > 0;

  // How long the simulation is allowed to run. A frozen layout runs no ticks
  // at all: every node is pinned, so a tick can only cost a frame. Grouped
  // into one branch rather than two ternaries in the JSX below, which is a
  // readability point and also keeps this component off another complexity
  // warning it does not need.
  // Nothing to warm up or cool down: every node is pinned in both modes.
  const ticks = { warmup: 0, cooldown: 0 };

  const hoverCard = useMemo(() => {
    const fg = graphRef.current;
    if (!fg || hoveredId == null) return null;
    const node = graphData.nodes.find(n => n.id === hoveredId);
    if (!node || node.x == null || node.y == null) return null;
    const { x, y } = fg.graph2ScreenCoords(node.x, node.y);
    return (
      <GraphHoverCard
        concept={node}
        lesson={subjectLabels?.get(node.subject)}
        x={x}
        y={y}
        containerWidth={dimensions.width}
      />
    );
    // `camera` is a dependency on purpose: the card follows the dot as the
    // map pans and zooms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredId, graphData, subjectLabels, dimensions.width, camera]);

  return (
    <div
      ref={containerRef}
      className={FRAME[variant]}
      // Present only once the graph is actually mounted. A story waits on this
      // rather than on the canvas element, because a canvas is in the DOM
      // before it has painted anything.
      data-graph-ready={isMeasured ? 'true' : undefined}
      // The camera has landed on the whole graph, and how many nodes (labels
      // included) are fully on the canvas under it. A canvas has no DOM, so
      // this is the only way a story can check the frame shows every node.
      data-graph-framed={autoFit.framed ? 'true' : undefined}
      data-nodes-in-view={autoFit.nodesInView ?? undefined}
      onWheelCapture={autoFit.release}
      onTouchMoveCapture={autoFit.release}
    >
      {/* Deep space behind everything, dark mode only. Rendered first so it
          sits under the graph's transparent canvas. */}
      {isDark && !isFixed && isMeasured && (
        <SpaceBackdrop
          width={dimensions.width}
          height={dimensions.height}
          camera={camera}
          rounded={!phone}
        />
      )}
      {isMeasured && (
        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          // Keep painting while a fade is in flight, so it finishes even when
          // the pointer stops; idle again once it has.
          autoPauseRedraw={!animating}
          width={dimensions.width}
          height={dimensions.height}
          // Settled-on-first-paint: run the simulation invisibly for
          // `warmupTicks` ticks before the first frame; freeze it after
          // `cooldownTicks` once visible so CPU is freed. Bumped past
          // our earlier values to match the Context7-documented
          // reference config for graphs of our size - gives a more
          // thoroughly settled layout before the user sees it.
          warmupTicks={ticks.warmup}
          cooldownTicks={ticks.cooldown}
          onEngineTick={autoFit.onEngineTick}
          onEngineStop={() => {
            enforceFixedViewport(graphRef.current?.zoom() ?? 1);
            autoFit.onEngineStop();
          }}
          d3AlphaDecay={0.025}
          d3VelocityDecay={0.35}
          // No `d3AlphaMin` - leaving the simulation able to drop down
          // to the d3-force default min so drag-induced motion actually
          // propagates to neighbour nodes for long enough to look
          // natural. With a high alpha floor the sim refroze almost
          // instantly after a drag and neighbours barely reacted.
          // Visual customisation (replaces the library's default node).
          // react-force-graph types these over its internal NodeObject;
          // drawNode/drawNodePointerArea take our narrowed node type.
          nodeCanvasObject={drawNode as never}
          nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={drawNodePointerArea as never}
          // Suppress the library's default hover tooltip - the inline
          // canvas label below the node already shows the name. An
          // empty function disables the tooltip entirely.
          nodeLabel={() => ''}
          // Links
          linkColor={getLinkColor}
          linkWidth={getLinkWidth}
          // Subtle curve on every edge - Obsidian-style; also lets
          // parallel edges between the same node pair render distinctly.
          linkCurvature={getLinkCurvature}
          // Interaction
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          onBackgroundClick={onBackgroundClick}
          onRenderFramePre={(ctx, k) => {
            drawGlow(ctx);
            drawConstellations(ctx, k);
          }}
          onRenderFramePost={(ctx, k) => {
            drawSubjectLabels(ctx, k);
            drawConceptLabels(ctx, k);
            drawResting(ctx, k);
          }}
          // Nodes are not draggable. Every position is assigned by
          // `layoutClusters` and pinned with `fx`/`fy`, so a drag does not
          // nudge a simulation — it overwrites the pin and permanently moves
          // the concept out of its cluster for the life of the mount. The
          // layout being the same on every reload is the premise of the view.
          enableNodeDrag={false}
          // Track zoom so the bottom-left badge can show the current
          // scale. `onZoom` fires for both wheel zoom + programmatic
          // calls like zoomToFit.
          onZoom={({ k, x, y }) => {
            setCamera({ k, cx: x, cy: y });
            enforceFixedViewport(k);
          }}
          nodeVisibility={nodeIsVisible as never}
          linkVisibility={linkIsVisible as never}
          // Minimum zoom prevents the user from getting completely lost.
          minZoom={0.2}
          maxZoom={8}
        />
      )}

      {/* Desktop only: a phone's canvas is already carrying the peek card and
          two round controls, and the guide explains the same thing in words. */}
      {!phone && hoverCard}
      {!phone && <ConstellationGuide progress={constellationProgress} />}

      <GraphControls
        phone={phone}
        zoom={zoom}
        onFit={fitToViewport}
        onHelp={openHelp}
        onZoomBy={zoomBy}
        showPaused={hasPaused}
      />

      <GraphHelpModal
        touch={phone}
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * After the simulation starts, react-force-graph rewrites `link.source`
 * and `link.target` from string ids to live node references. Read the
 * id off either shape.
 */
/**
 * One soft blob into the glow image: the lesson's seeded outline
 * (`blobOutline`), smoothed, filled with a radial gradient through `stops`
 * (centre, 70%, rim alpha) and blurred by `blur` times its radius.
 */
function paintBlob(
  ctx: CanvasRenderingContext2D,
  seed: string,
  cx: number,
  cy: number,
  radius: number,
  [r, g, b]: readonly [number, number, number],
  { stops, blur }: { stops: [number, number, number]; blur: number }
) {
  const outline = blobOutline(seed, cx, cy, radius);
  const fill = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  fill.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${stops[0]})`);
  fill.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${stops[1]})`);
  fill.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${stops[2]})`);
  // Soft edge where the browser supports `filter` (Safari ignores it and
  // gets a crisper rim, which is fine).
  ctx.filter = `blur(${Math.max(1.5, radius * blur)}px)`;
  // Smooth through the outline points: midpoints as anchors, points as
  // control points, so the rim is a curve rather than straight segments.
  const mid = (i: number) => {
    const p = outline[i % outline.length];
    const q = outline[(i + 1) % outline.length];
    return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
  };
  ctx.beginPath();
  const start = mid(0);
  ctx.moveTo(start.x, start.y);
  for (let i = 1; i <= outline.length; i++) {
    const p = outline[i % outline.length];
    const m = mid(i);
    ctx.quadraticCurveTo(p.x, p.y, m.x, m.y);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

/**
 * Hover and selection: a card-coloured gap and then the brand ring, so it
 * reads the same around an indigo, grey, amber or hollow node.
 */
function drawFocusRing(
  ctx: CanvasRenderingContext2D,
  node: { x?: number; y?: number },
  size: number,
  globalScale: number,
  gap: string,
  ring: string
) {
  if (node.x == null || node.y == null) return;
  ctx.beginPath();
  ctx.arc(node.x, node.y, size + 1.2 / globalScale, 0, 2 * Math.PI);
  ctx.lineWidth = 1.2 / globalScale;
  ctx.strokeStyle = gap;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(node.x, node.y, size + 2.4 / globalScale, 0, 2 * Math.PI);
  ctx.lineWidth = 1.4 / globalScale;
  ctx.strokeStyle = ring;
  ctx.stroke();
}

function idOf(endpoint: unknown): string {
  if (typeof endpoint === 'string') return endpoint;
  if (endpoint && typeof endpoint === 'object' && 'id' in endpoint) {
    return String((endpoint as { id: unknown }).id);
  }
  return '';
}
